// Shared "Team Rating + Notes" section for the Team Directory and the
// Vendor Directory. One place owns the look and the rules so the two pages
// never drift: anyone can add a note and cast one 1..5 star vote; a note can
// be edited or deleted by its author or the Team Lead; legacy unattributed
// notes are read-only.
//
// Edits and deletes are addressed by the note's IDENTITY (utils/entryNotes
// noteRef), never by its position — positions shift whenever anyone adds or
// deletes, including other users on other machines. The host page's
// onEditNote/onDeleteNote must resolve the ref against FRESH data at save
// time (utils/directoryNotes + utils/vendorDirectory do) and may return
// null to signal "that note changed under you".
import { useEffect, useState } from "react";
import { Star, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useActionDialog } from "./ActionDialog";
import { formatDate } from "../utils/helper";
import {
  buildNote,
  noteList,
  noteRef,
  entryMatchesRef,
  canManageNote,
  ratingSummary,
  myRating,
} from "../utils/entryNotes";

// Row of five stars. Read-only unless onSelect is passed; `value` may be
// fractional for display (a star fills when the value reaches its half).
export function StarRow({ value, onSelect, size = 16, activeColor = "#f59e0b" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n - 0.5;
        const star = (
          <Star
            size={size}
            fill={filled ? activeColor : "none"}
            stroke={filled ? activeColor : "#cbd5e1"}
          />
        );
        return onSelect ? (
          <button
            key={n}
            type="button"
            onClick={() => onSelect(n)}
            title={`${n} Star${n === 1 ? "" : "s"}`}
            className="hover:scale-110 transition-transform"
          >
            {star}
          </button>
        ) : (
          <span key={n}>{star}</span>
        );
      })}
    </span>
  );
}

// Compact average badge for cards and list rows: ★ 4.5 (6). Renders nothing
// until someone has rated, so unrated entries stay clean.
export function RatingBadge({ ratings }) {
  const { average, count } = ratingSummary(ratings);
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
      <Star size={11} fill="#f59e0b" stroke="#f59e0b" />
      <span className="font-semibold text-slate-700">{average}</span>({count})
    </span>
  );
}

export default function EntryNotesSection({
  entries,
  legacyNotes,
  ratings,
  user,
  onAddNote, // async (note) => void
  onEditNote, // async (ref, newText) => void; may return null = note vanished
  onDeleteNote, // async (ref) => void; may return null = note vanished
  onRate, // async (stars) => void
}) {
  const { confirm, dialog } = useActionDialog();
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editRef, setEditRef] = useState(null); // noteRef being edited
  const [editText, setEditText] = useState("");

  const list = noteList(entries, legacyNotes);
  const { average, count } = ratingSummary(ratings);
  const mine = myRating(ratings, user && user.id);
  // Without a signed-in user we can't attribute anything — read-only view.
  const canWrite = !!(user && user.id);

  // If the note being edited disappears from the live list (someone else
  // deleted it), close the editor instead of letting the edit land wrong.
  useEffect(() => {
    if (!editRef) return;
    const stillThere = (Array.isArray(entries) ? entries : []).some((n) =>
      entryMatchesRef(n, editRef),
    );
    if (!stillThere) {
      setEditRef(null);
      setEditText("");
      toast.info("That note was changed by someone else.");
    }
  }, [entries, editRef]);

  const run = async (fn, failMessage) => {
    if (busy) {
      toast.info("Hold on — still saving the last change.");
      return;
    }
    setBusy(true);
    try {
      const result = await fn();
      if (result === null) {
        toast.info("That note was changed by someone else.");
      }
    } catch (err) {
      console.error(failMessage, err);
      toast.error(failMessage);
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const text = noteText.trim();
    if (!text || !canWrite) return;
    run(async () => {
      await onAddNote(buildNote(text, user));
      setNoteText("");
    }, "Couldn't save that note");
  };

  const saveEdit = () => {
    const text = editText.trim();
    if (!text || !editRef) return;
    const ref = editRef;
    run(async () => {
      const result = await onEditNote(ref, text);
      setEditRef(null);
      setEditText("");
      return result;
    }, "Couldn't save the edit");
  };

  const remove = async (n) => {
    const ref = noteRef(n);
    const ok = await confirm({
      title: "Delete Note",
      body: `Delete this note${n.authorName ? ` by ${n.authorName}` : ""}? This can't be undone.`,
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    run(() => onDeleteNote(ref), "Couldn't delete the note");
  };

  const isEditing = (n) => editRef && entryMatchesRef(n, editRef);

  return (
    <div>
      {/* ── Team Rating ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Team Rating</p>
          {count ? (
            <span className="inline-flex items-center gap-1.5">
              <StarRow value={average} />
              <span className="text-sm font-semibold text-slate-700">
                {average}
              </span>
              <span className="text-xs text-slate-400">
                ({count} {count === 1 ? "rating" : "ratings"})
              </span>
            </span>
          ) : (
            <p className="text-xs text-slate-400">No ratings yet.</p>
          )}
        </div>
        {onRate && canWrite && (
          <div className="text-right">
            <p className="text-xs font-medium text-slate-500 mb-1">
              Your Rating
            </p>
            <StarRow
              value={mine}
              size={18}
              onSelect={(n) =>
                run(() => onRate(n), "Couldn't save your rating")
              }
            />
          </div>
        )}
      </div>

      {/* ── Notes ── */}
      <p className="text-xs font-medium text-slate-500 mb-2">Notes</p>
      {canWrite && (
        <div className="flex gap-2 mb-4">
          <input
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1E293B]"
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
          />
          <button
            type="button"
            disabled={busy || !noteText.trim()}
            onClick={add}
            className="text-sm px-4 rounded-md bg-[#1E293B] text-white disabled:opacity-40 flex-shrink-0"
          >
            {busy ? "Saving" : "Add"}
          </button>
        </div>
      )}

      {list.length === 0 ? (
        <p className="text-xs text-slate-400">No notes yet.</p>
      ) : (
        <div className="space-y-3">
          {list.map((n, i) =>
            isEditing(n) ? (
              <div key={n.id || `e-${i}`} className="border-l-2 border-[#1E293B] pl-3">
                <textarea
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1E293B]"
                  rows={2}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autoFocus
                />
                <div className="flex gap-2 mt-1.5">
                  <button
                    type="button"
                    disabled={busy || !editText.trim()}
                    onClick={saveEdit}
                    className="text-xs px-3 py-1 rounded-md bg-[#1E293B] text-white disabled:opacity-40"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditRef(null);
                      setEditText("");
                    }}
                    className="text-xs px-3 py-1 rounded-md border border-slate-300 text-slate-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={n.id || (n.legacy ? "legacy" : `n-${i}`)}
                className="group/note border-l-2 border-slate-200 pl-3"
              >
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {n.text}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2">
                  <span>
                    {n.authorName || "Author not recorded"}
                    {n.createdAt ? ` · ${formatDate(n.createdAt)}` : ""}
                  </span>
                  {canManageNote(n, user) && (
                    <span className="inline-flex gap-1.5 opacity-0 group-hover/note:opacity-100 transition-opacity">
                      <button
                        type="button"
                        title="Edit Note"
                        disabled={busy}
                        onClick={() => {
                          setEditRef(noteRef(n));
                          setEditText(n.text);
                        }}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        title="Delete Note"
                        disabled={busy}
                        onClick={() => remove(n)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 size={12} />
                      </button>
                    </span>
                  )}
                </p>
              </div>
            ),
          )}
        </div>
      )}
      {dialog}
    </div>
  );
}
